import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Loader2,
  Star,
  User,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { validationService } from "../services/api";

const ValidateQR = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [parentData, setParentData] = useState(null);
  const [activites, setActivites] = useState([]);
  const [selectedActivite, setSelectedActivite] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [error, setError] = useState(null);

  const qrCode = searchParams.get("qr_code");
  const codeUnique = searchParams.get("code_unique");

  useEffect(() => {
    if (!qrCode && !codeUnique) {
      setError("QR code ou code unique manquant");
      setLoading(false);
      return;
    }

    // Si l'utilisateur n'est pas connecté, rediriger vers login
    if (!user) {
      const redirectUrl = qrCode
        ? `/validate?qr_code=${qrCode}`
        : `/validate?code_unique=${codeUnique}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    // Vérifier que l'utilisateur est admin
    if (user.role !== "admin" && user.role !== "superadmin") {
      setError(
        "Accès non autorisé. Seuls les administrateurs peuvent valider des tampons."
      );
      setLoading(false);
      return;
    }

    loadQRData();
  }, [qrCode, codeUnique, user, navigate]);

  const loadQRData = async () => {
    try {
      setLoading(true);
      let response;

      if (qrCode) {
        response = await validationService.validateQR(qrCode);
      } else if (codeUnique) {
        response = await validationService.validateCodeUnique(codeUnique);
      }

      const data = response.data;

      setParentData(data.parent);
      setActivites(data.activites);

      // Sélectionner la première activité par défaut
      if (data.activites.length > 0) {
        setSelectedActivite(data.activites[0].id.toString());
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      if (error.response?.status === 404) {
        setError(
          qrCode
            ? "QR code non trouvé ou parent inactif"
            : "Code unique non trouvé ou parent inactif"
        );
      } else {
        setError("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async () => {
    if (!selectedActivite) {
      toast.error("Veuillez sélectionner une activité");
      return;
    }

    try {
      setValidating(true);

      // Utiliser le code disponible (QR code ou code unique)
      const codeToUse = qrCode || codeUnique;

      const response = await validationService.confirmValidation(
        codeToUse,
        parseInt(selectedActivite),
        commentaire || null
      );

      const tamponId = response.data.id;
      toast.success("Tampon validé avec succès !");

      // Rediriger vers la page de succès
      navigate(`/validate/success/${tamponId}`);
    } catch (error) {
      console.error("Erreur lors de la validation:", error);
      toast.error("Erreur lors de la validation du tampon");
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Erreur de validation
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/app/admin")}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/app/admin")}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au dashboard
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Validation de Tampon
          </h1>
          <p className="text-gray-600">
            Validez la participation d'un parent à une activité
          </p>
        </div>

        {/* Informations du parent */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <User className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Informations du parent
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom du parent
              </label>
              <p className="text-gray-900 font-medium">{parentData?.nom}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-gray-900">{parentData?.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'enfant
              </label>
              <p className="text-gray-900">{parentData?.nom_enfant}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classe
              </label>
              <p className="text-gray-900">{parentData?.classe_enfant}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code unique
              </label>
              <p className="text-gray-900 font-mono">
                {parentData?.code_unique}
              </p>
            </div>
          </div>
        </div>

        {/* Sélection de l'activité */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <Calendar className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Sélection de l'activité
            </h2>
          </div>

          <div className="space-y-3">
            {activites.map((activite) => (
              <label
                key={activite.id}
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedActivite === activite.id.toString()
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="activite"
                  value={activite.id}
                  checked={selectedActivite === activite.id.toString()}
                  onChange={(e) => setSelectedActivite(e.target.value)}
                  className="sr-only"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">
                      {activite.nom}
                    </h3>
                    <div className="flex items-center text-yellow-600">
                      <Star className="h-4 w-4 mr-1" />
                      <span className="font-medium">{activite.points} pts</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 capitalize">
                    {activite.type_activite}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Commentaire */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Commentaire (optionnel)
          </label>
          <textarea
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            placeholder="Ajoutez un commentaire sur cette validation..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
          />
        </div>

        {/* Bouton de validation */}
        <div className="flex space-x-4">
          <button
            onClick={() => navigate("/app/admin")}
            className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>

          <button
            onClick={handleValidation}
            disabled={validating || !selectedActivite}
            className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {validating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Validation...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Valider le tampon
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidateQR;
